import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { useTheme } from '../App';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import { logoDarkmode, logoLightmode } from '../assets';
import { useNavigate, useSearchParams } from 'react-router-dom';

const Auth = () => {
  const { darkMode } = useTheme();
  const { login, signup, signInWithGoogle, requestPasswordReset, updatePassword } = useUser();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Set page title
  useEffect(() => {
    document.title = 'Sign In - Idealyze';
    return () => {
      document.title = 'Idealyze';
    };
  }, []);

  // Handle OAuth callback and password reset
  useEffect(() => {
    const handleAuthCallback = async () => {
      const isCallback = searchParams.get('callback');
      const isReset = searchParams.get('reset');

      // Handle password reset from email link
      if (isReset) {
        setShowPasswordReset(true);
        setIsLogin(false);
        return;
      }

      if (isCallback) {
        try {
          // Import supabase and get session from the callback
          const { supabase } = await import('../lib/supabase');
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Supabase session error:', error);
            setError('Authentication failed. Please try again.');
            return;
          }

          if (data?.session) {
            // Store the token and let UserContext handle the rest
            localStorage.setItem('token', data.session.access_token);
            console.log('OAuth callback successful, redirecting to dashboard...');
            // Force a page reload to trigger UserContext initialization
            window.location.href = '/dashboard';
          } else {
            // Fallback: try to get token from URL hash
            const accessToken = new URLSearchParams(window.location.hash.substring(1)).get('access_token');
            if (accessToken) {
              localStorage.setItem('token', accessToken);
              console.log('Token found in URL hash, redirecting to dashboard...');
              window.location.href = '/dashboard';
            } else {
              setError('No authentication token found. Please try again.');
            }
          }
        } catch (error) {
          console.error('OAuth callback error:', error);
          setError('Authentication failed. Please try again.');
        }
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        if (formData.password !== formData.confirmPassword) {
          setError(t('auth.passwordsDoNotMatch'));
          setLoading(false);
          return;
        }
        await signup(formData.name, formData.email, formData.password);
      }
    } catch (error) {
      // Handle rate limiting and other specific errors
      if (error.message.includes('Too many') || error.message.includes('rate limit')) {
        setError(t('auth.rateLimitError'));
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      await signInWithGoogle();
    } catch (error) {
      // Handle rate limiting and other specific errors
      if (error.message.includes('Too many') || error.message.includes('rate limit')) {
        setError(t('auth.rateLimitError'));
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await requestPasswordReset(resetEmail);
      setSuccessMessage(t('auth.resetEmailSent'));
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (newPassword !== confirmNewPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError(t('auth.passwordTooShort'));
      setLoading(false);
      return;
    }

    try {
      await updatePassword(newPassword);
      setSuccessMessage(t('auth.passwordResetSuccess'));
      setTimeout(() => {
        setShowPasswordReset(false);
        setIsLogin(true);
        navigate('/auth');
      }, 2000);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`relative flex min-h-screen w-full items-center justify-center overflow-hidden p-4 ${
      darkMode ? 'bg-gradient-soft-dark' : 'bg-gradient-soft-light'
    }`}>
      {/* Language Toggle Button */}
      <div className="absolute top-4 right-4 z-50">
        <LanguageToggle className="w-10 h-10 p-2" />
      </div>
      <style>{`
        .auth-btn {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          position: relative;
          overflow: hidden;
        }
        .auth-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
          );
          transition: left 0.5s;
        }
        .auth-btn:hover::before {
          left: 100%;
        }
      `}</style>
      
      <div className="z-10 w-full max-w-6xl">
        <div className={`overflow-hidden rounded-[40px] shadow-2xl ${
          darkMode ? 'bg-gray-800/50' : 'bg-white/50'
        }`}>
          <div className={`grid min-h-[700px] lg:grid-cols-2 ${isRTL ? 'lg:direction-rtl' : ''}`}>
            {/* Left Side - Brand */}
            <div className={`brand-side relative m-4 rounded-3xl p-12 ${
              darkMode ? 'bg-gradient-to-br from-gray-100 to-gray-400 text-gray-800' : 'bg-gradient-to-br from-gray-700 to-gray-900 text-white'
            } ${isRTL ? 'order-2 lg:order-1' : ''}`}>
              <div>
                <div className={`mb-8 flex items-center ${isRTL ? 'justify-end -mr-8' : 'justify-start -ml-8'}`}>
                  <div className="flex items-center justify-center w-56 h-32">
                    <img 
                      src={darkMode ? logoLightmode : logoDarkmode}
                      alt="Idealyze Logo" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                
                <h1 className={`mb-4 text-3xl font-medium ${
                  darkMode ? 'text-gray-800' : 'text-white'
                }`}>
                  {t('auth.tagline')}
                </h1>
                <p className="mb-12 text-lg opacity-80">
                  {t('auth.subtitle')}
                </p>
                
                <div className="space-y-6">
                  {[
                    {
                      icon: (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      ),
                      titleKey: 'auth.features.dailyIdeas.title',
                      descKey: 'auth.features.dailyIdeas.description'
                    },
                    {
                      icon: (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      ),
                      titleKey: 'auth.features.ideasVault.title',
                      descKey: 'auth.features.ideasVault.description'
                    },
                    {
                      icon: (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      ),
                      titleKey: 'auth.features.buildersStudio.title',
                      descKey: 'auth.features.buildersStudio.description'
                    },
                    {
                      icon: (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      ),
                      titleKey: 'auth.features.analyticsDashboard.title',
                      descKey: 'auth.features.analyticsDashboard.description'
                    }
                  ].map(({ icon, titleKey, descKey }, i) => (
                    <div
                      key={i}
                      className="feature-item flex items-center fade-in-soft"
                      style={{ animationDelay: `${0.2 * (i + 1)}s` }}
                    >
                      <div className={`flex h-8 w-8 items-center justify-center rounded-lg backdrop-blur-sm ${
                        darkMode ? 'bg-gray-800/30 text-gray-800' : 'bg-white/20 text-white'
                      } ${isRTL ? 'ml-4' : 'mr-4'}`}>
                        {icon}
                      </div>
                      <div>
                        <div className="font-semibold">{t(titleKey)}</div>
                        <div className="text-sm opacity-70">{t(descKey)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className={`flex flex-col justify-center p-12 ${isRTL ? 'order-1 lg:order-2' : ''}`}>
              <div className="mx-auto w-full max-w-md">
                <div className="mb-8 text-center">
                  <h2 className={`text-3xl font-light uppercase ${
                    darkMode ? 'text-white' : 'text-slate-700'
                  }`}>
                    {isLogin ? t('auth.welcomeBack') : t('auth.createAccount')}
                  </h2>
                  <p className={`mt-2 text-sm ${
                    darkMode ? 'text-gray-300' : 'text-slate-600'
                  }`}>
                    {isLogin 
                      ? t('auth.signInSubtitle') 
                      : t('auth.createAccountSubtitle')
                    }
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {!isLogin && (
                    <div>
                      <label htmlFor="name" className={`mb-2 block text-sm font-medium uppercase ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>
                        {t('auth.fullName')}
                      </label>
                      <div className="relative">
                        <input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="input-soft w-full"
                          placeholder={t('auth.fullNamePlaceholder')}
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="email" className={`mb-2 block text-sm font-medium uppercase ${
                      darkMode ? 'text-gray-300' : 'text-slate-600'
                    }`}>
                      {t('auth.email')}
                    </label>
                    <div className="relative">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="input-soft w-full"
                        placeholder={t('auth.emailPlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className={`mb-2 block text-sm font-medium uppercase ${
                      darkMode ? 'text-gray-300' : 'text-slate-600'
                    }`}>
                      {t('auth.password')}
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        className={`input-soft w-full ${isRTL ? 'pl-12' : 'pr-12'}`}
                        placeholder={t('auth.passwordPlaceholder')}
                      />
                      <button
                        type="button"
                        className={`absolute inset-y-0 flex items-center ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'}`}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {!isLogin && (
                    <div>
                      <label htmlFor="confirmPassword" className={`mb-2 block text-sm font-medium uppercase ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>
                        {t('auth.confirmPassword')}
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          required
                          className={`input-soft w-full ${isRTL ? 'pl-12' : 'pr-12'}`}
                          placeholder={t('auth.confirmPasswordPlaceholder')}
                        />
                        <button
                          type="button"
                          className={`absolute inset-y-0 flex items-center ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'}`}
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {isLogin && (
                    <div className="flex items-center justify-between">
                      <label className={`flex items-center text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className={isRTL ? 'mr-2' : 'ml-2'}>{t('auth.rememberMe')}</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-indigo-600 hover:text-indigo-500"
                      >
                        {t('auth.forgotPassword')}
                      </button>
                    </div>
                  )}

                  {error && (
                    <div className="text-red-500 text-sm text-center">{error}</div>
                  )}

                  {successMessage && (
                    <div className="text-green-500 text-sm text-center">{successMessage}</div>
                  )}

                  <button
                    type="submit"
                    className="auth-btn relative flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-white transition-all duration-300"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>{isLogin ? t('auth.signingIn') : t('auth.creatingAccount')}</span>
                      </>
                    ) : (
                      isLogin ? t('auth.signIn') : t('auth.createAccount')
                    )}
                  </button>

                  <div className={`relative text-center text-sm ${
                    darkMode ? 'text-gray-400' : 'text-slate-500'
                  }`}>
                    <div className="absolute inset-0 flex items-center">
                      <div className={`w-full border-t ${
                        darkMode ? 'border-gray-600' : 'border-gray-300'
                      }`}></div>
                    </div>
                    <span className={`relative px-2 ${
                      darkMode ? 'bg-gray-800' : 'bg-white'
                    }`}>{t('auth.orContinueWith')}</span>
                  </div>

                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                      className={`flex items-center justify-center rounded-lg border px-6 py-3 text-sm shadow-sm hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed w-full max-w-xs ${
                        darkMode 
                          ? 'border-gray-600 bg-gray-700 text-white' 
                          : 'border-gray-300 bg-white text-slate-700'
                      }`}
                    >
                      <img
                        src="https://www.svgrepo.com/show/475656/google-color.svg"
                        className="h-5 w-5"
                        alt="Google"
                      />
                      <span className={isRTL ? 'mr-2' : 'ml-2'}>{t('auth.google')}</span>
                    </button>
                  </div>
                </form>

                <div className={`mt-8 text-center text-sm ${
                  darkMode ? 'text-gray-400' : 'text-slate-500'
                }`}>
                  {isLogin ? t('auth.dontHaveAccount') : t('auth.alreadyHaveAccount')}{' '}
                  <button
                    onClick={() => setIsLogin(!isLogin)}
                    className="text-indigo-600 hover:text-indigo-500 font-medium"
                  >
                    {isLogin ? t('auth.signUpFree') : t('auth.signIn')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full rounded-lg p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('auth.forgotPassword')}
              </h2>
              <p className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('auth.resetPasswordInstructions')}
              </p>
            </div>

            <form onSubmit={handleForgotPassword}>
              <div className="mb-4">
                <label htmlFor="resetEmail" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('auth.email')}
                </label>
                <input
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="input-soft w-full"
                  placeholder={t('auth.emailPlaceholder')}
                />
              </div>

              {error && (
                <div className="text-red-500 text-sm text-center mb-4">{error}</div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                    setError('');
                  }}
                  className={`flex-1 py-2 px-4 rounded-lg border transition-colors ${
                    darkMode
                      ? 'border-gray-600 bg-gray-700 text-white hover:bg-gray-600'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading || !resetEmail}
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? t('common.sending') : t('auth.sendResetEmail')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Form */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full rounded-lg p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('auth.resetPassword')}
              </h2>
              <p className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('auth.enterNewPassword')}
              </p>
            </div>

            <form onSubmit={handlePasswordReset}>
              <div className="mb-4">
                <label htmlFor="newPassword" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('auth.newPassword')}
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="input-soft w-full"
                  placeholder={t('auth.newPasswordPlaceholder')}
                />
              </div>

              <div className="mb-6">
                <label htmlFor="confirmNewPassword" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('auth.confirmNewPassword')}
                </label>
                <input
                  id="confirmNewPassword"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="input-soft w-full"
                  placeholder={t('auth.confirmNewPasswordPlaceholder')}
                />
              </div>

              {error && (
                <div className="text-red-500 text-sm text-center mb-4">{error}</div>
              )}

              {successMessage && (
                <div className="text-green-500 text-sm text-center mb-4">{successMessage}</div>
              )}

              <button
                type="submit"
                disabled={loading || !newPassword || !confirmNewPassword}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? t('common.updating') : t('auth.updatePassword')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;